import type { MatrixData } from '../components/MatrixTable'
import type { ProductionSpeciesKey } from '../data/productionSpeciesPresets'
import type { ProductionUnit } from '../types/farm'
import type { WizardFarm, WizardProductionUnit } from '../types/farmWizard'
import type { Lot } from '../types/lot'

import { createMatrixProfileStore, type MatrixProfileRecord, type ResolvedMatrixProfileSelection } from './matrixProfiles'
import { getDefaultMatrixForSpecies } from './mortalityMatrix'

export type MortalityProfileRecord = MatrixProfileRecord
export type ResolvedMortalityProfileSelection = ResolvedMatrixProfileSelection

const store = createMatrixProfileStore(
  {
    profilesKey: 'mortalityProfiles',
    defaultAssignmentsKey: 'mortalityDefaultProfileAssignments',
    assignmentsKey: 'mortalityProfileAssignments'
  },
  getDefaultMatrixForSpecies
)

export const listFarmMortalityProfiles = (
  farm: WizardFarm | undefined,
  species?: ProductionSpeciesKey
): MortalityProfileRecord[] => store.listFarmProfiles(farm, species)

export const findFarmMortalityProfile = (
  farm: WizardFarm | undefined,
  profileId: string | null | undefined
): MortalityProfileRecord | null => store.findFarmProfile(farm, profileId)

export const readFarmDefaultMortalityProfileId = (
  farm: WizardFarm | undefined,
  species: ProductionSpeciesKey
): string | null => store.readFarmDefaultProfileId(farm, species)

export const isFarmDefaultMortalityProfile = (
  farm: WizardFarm | undefined,
  species: ProductionSpeciesKey,
  profileId: string | null | undefined
): boolean => store.isFarmDefaultProfile(farm, species, profileId)

export const upsertFarmMortalityProfile = (
  farm: WizardFarm,
  profile: MortalityProfileRecord
): WizardFarm => store.upsertFarmProfile(farm, profile)

export const removeFarmMortalityProfile = (
  farm: WizardFarm,
  profileId: string
): WizardFarm => store.removeFarmProfile(farm, profileId)

export const setFarmDefaultMortalityProfileId = (
  farm: WizardFarm,
  species: ProductionSpeciesKey,
  profileId: string | null | undefined
): WizardFarm => store.setFarmDefaultProfileId(farm, species, profileId)

export const readAssignedMortalityProfileId = (
  target: Pick<ProductionUnit, 'metadata'> | Pick<Lot, 'metadata'> | undefined,
  species: ProductionSpeciesKey
): string | null => store.readAssignedProfileId(target, species)

export const setProductionUnitMortalityProfileAssignment = (
  unit: WizardProductionUnit,
  species: ProductionSpeciesKey,
  profileId: string | null | undefined
): WizardProductionUnit => store.setProductionUnitProfileAssignment(unit, species, profileId)

export const setLotMortalityProfileAssignment = (
  lot: Lot,
  species: ProductionSpeciesKey,
  profileId: string | null | undefined
): Lot => store.setLotProfileAssignment(lot, species, profileId)

export const resolveMortalityProfileSelection = (options: {
  farm?: WizardFarm
  productionUnit?: ProductionUnit
  lot?: Lot
  species: ProductionSpeciesKey
}): ResolvedMortalityProfileSelection => store.resolveProfileSelection(options)

export const resolveMortalityMatrixForSelection = (
  selection: ResolvedMortalityProfileSelection,
  species: ProductionSpeciesKey,
  speciesDefaultMatrix?: MatrixData | null
): MatrixData => store.resolveMatrixForSelection(selection, species, speciesDefaultMatrix)

export const resolveEffectiveMortalityMatrix = (options: {
  farm?: WizardFarm
  productionUnit?: ProductionUnit
  lot?: Lot
  species: ProductionSpeciesKey
  speciesDefaultMatrix?: MatrixData | null
}): MatrixData => store.resolveEffectiveMatrix(options)
